/**
 * @file: MessageList.tsx
 *
 * Backend selector for the conversation's message list. Both backends honour
 * the same `MessageListProps`, so swapping is a one-line flip — `FlatList` is
 * the proven fallback, `FlashList` the recycling backend for smooth scrolling
 * over long, image-heavy histories.
 *
 * FlashList recycles rows for smooth scrolling over long, image-heavy
 * histories on native. Its inverted-list support on web is unreliable, so the
 * web (desktop master-detail) stays on the proven FlatList backend.
 */

import { FC } from 'react';
import { Platform } from 'react-native';
import { MessageListFlashList } from './MessageListFlashList';
import { MessageListFlatList } from './MessageListFlatList';
import { MessageListProps } from './messageListShared';

const MESSAGE_LIST_BACKEND: 'flashlist' | 'flatlist' =
  Platform.OS === 'web' ? 'flatlist' : 'flashlist';

export const MessageList: FC<MessageListProps> =
  MESSAGE_LIST_BACKEND === 'flashlist'
    ? MessageListFlashList
    : MessageListFlatList;

/**
 * Whether the time marker must be rendered BEFORE its message (vs after) for it
 * to appear visually above it. FlatList's inverted cell reverses child order
 * (so the marker is rendered last); FlashList's doesn't, so it's rendered first.
 */
export const MESSAGE_LIST_TIME_MARKER_FIRST =
  MESSAGE_LIST_BACKEND === 'flashlist';

export type { MessageListRef } from './messageListShared';
